import {
  FieldWrapper,
  FieldWrapperProps,
  FlexBox,
  InputText,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import * as React from 'react';

import { durationConverter } from 'utils/duration';

import styles from './duration.module.scss';

enum TimeUnit {
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
}

type OwnProps = {
  duration?: number;
  withHours?: boolean;
  withDays?: boolean;
  onChange: (duration: number) => void;
};

type Props = OwnProps & Omit<FieldWrapperProps, 'onChange'>;

type State = {
  [Key in TimeUnit]?: number;
};

export function Duration({
  className,
  duration,
  withHours,
  withDays,
  onChange,
  ...rest
}: Props): JSX.Element {
  const [state, setState] = useState<State>(
    durationConverter.splitTimeUnits(duration || 0, true),
  );

  const handleTimeChange = useCallback(
    (name: TimeUnit) =>
      ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        setState((currentState) => ({
          ...currentState,
          ...{ [name]: isNaN(parseInt(value)) ? undefined : parseInt(value) },
        }));
      },
    [],
  );

  const handleInternalValueChange = useCallback(() => {
    setState((currentState) => {
      const { days = 0, hours = 0, minutes = 0, seconds = 0 } = currentState;
      const durationInSeconds = durationConverter.combineTimeUnits(
        days,
        hours,
        minutes,
        seconds,
      );

      onChange(durationInSeconds);

      if (withHours) {
        return durationConverter.splitTimeUnits(durationInSeconds, withDays);
      }

      return durationConverter.seconds.toMinutesAndSeconds(durationInSeconds);
    });
  }, [onChange, withDays, withHours]);

  const renderTimeInput = (name: TimeUnit) => {
    const unit = `${name.charAt(0).toUpperCase()}${name.slice(1)}`;

    return (
      <InputText
        className={styles.field}
        min="0"
        step="1"
        name={name}
        onChange={handleTimeChange(name)}
        onBlur={handleInternalValueChange}
        suffix={unit}
        type="number"
        value={state[TimeUnit[unit]]?.toString() || ''}
      />
    );
  };

  return (
    <FieldWrapper className={className} {...rest}>
      <FlexBox>
        {withDays && withHours && renderTimeInput(TimeUnit.Days)}
        {withHours && renderTimeInput(TimeUnit.Hours)}
        {renderTimeInput(TimeUnit.Minutes)}
        {renderTimeInput(TimeUnit.Seconds)}
      </FlexBox>
    </FieldWrapper>
  );
}
